import { useEffect, useMemo, useState } from 'react';
import { Table, Space, Button, Modal, Form, Input, Select, DatePicker, message, Collapse, Tag, Typography } from 'antd';
import type { TableProps } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, KeyOutlined } from '@ant-design/icons';
import {
  supabase,
  phoneToAuthEmail,
  provisionAuthUser,
  formatDate,
  formatDateTime,
  parseGeoPoint,
  compressImage,
  validateImageFile,
  getSignedUrl,
  useAuthUser,
  parseUserRole,
  isAdminRole,
  usePortalPermissions,
  useFleetOperator,
  formatVehicleAdminLabel,
  driverComplianceReadiness,
  summarizeActiveCompliance,
  complianceTagColor,
  complianceReadinessRank,
  isExpiryPast,
  sendPasswordSetupLink,
  logAssistResetAudit,
  driverResetPasswordRedirectUrl,
  SetupCredentialsNotice,
  invokeEdgeFunction,
} from '@ubus/shared';
import type { Driver, FleetOperator, Vehicle, ComplianceReadiness } from '@ubus/shared';
import dayjs from 'dayjs';
import EntityDrawer from '../components/EntityDrawer';
import AdminListPage from '../components/AdminListPage';
import ComplianceSummaryBar from '../components/ComplianceSummaryBar';
import StatusTag from '../components/StatusTag';
import { TableRowActions } from '../components/TableRowActions';
import { useAdminTableStability } from '../hooks/useAdminTableStability';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { localeConfirm, localeSuccess } from '../utils/localeModal';
import { formatAdminDbError } from '../utils/formatAdminDbError';

function confirmActiveExpired(
  t: TFunction,
  opts: {
    title: string;
    content: string;
  }
): Promise<boolean> {
  return new Promise((resolve) => {
    localeConfirm(t, {
      title: opts.title,
      content: opts.content,
      okType: 'danger',
      onOk: () => resolve(true),
      onCancel: () => resolve(false),
    });
  });
}

function Drivers() {
  const { t, i18n } = useTranslation();
  const user = useAuthUser();
  const isHqAdmin = isAdminRole(parseUserRole(user));
  const { operator, operatorId } = useFleetOperator();
  const { hasPermission } = usePortalPermissions();
  const canCreate = hasPermission('drivers.create');
  const canEdit = hasPermission('drivers.edit');
  const canDelete = hasPermission('drivers.delete');
  const canProvision = hasPermission('drivers.provision');
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [operators, setOperators] = useState<FleetOperator[]>([]);
  const operatorNameById = useMemo(() => {
    const map = Object.fromEntries(operators.map((o) => [o.id, o.name]));
    if (operator) map[operator.id] = operator.name;
    return map;
  }, [operators, operator]);
  const vehicleLabelById = useMemo(
    () =>
      Object.fromEntries(
        vehicles.map((v) => [v.id, formatVehicleAdminLabel(v, t('vehicles.plateMissing'))])
      ),
    [vehicles, t]
  );
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingDriver, setEditingDriver] = useState<Driver | null>(null);
  const [viewDriver, setViewDriver] = useState<Driver | null>(null);
  const [viewLicenseUrl, setViewLicenseUrl] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [complianceFilter, setComplianceFilter] = useState<ComplianceReadiness | undefined>(undefined);
  const [searchQuery, setSearchQuery] = useState('');
  const [form] = Form.useForm();
  const watchedOperatorId = Form.useWatch('fleet_operator_id', form) as string | undefined;
  const filterOperatorId = isHqAdmin ? watchedOperatorId : (operatorId ?? undefined);
  const [licenseFile, setLicenseFile] = useState<File | null>(null);
  const [licensePreview, setLicensePreview] = useState<string | null>(null);
  const [licenseUploading, setLicenseUploading] = useState(false);
  const { pagination, onTableChange, rowClassName, afterMutation } = useAdminTableStability();

  const preferredVehicleOptions = useMemo(
    () =>
      vehicles
        .filter((v) => !filterOperatorId || v.fleet_operator_id === filterOperatorId)
        .filter((v) => v.status === 'active')
        .map((v) => ({
          value: v.id,
          label: formatVehicleAdminLabel(v, t('vehicles.plateMissing')),
        })),
    [vehicles, filterOperatorId, t]
  );

  const driversWithReadiness = useMemo(
    () =>
      drivers.map((d) => ({
        ...d,
        readiness: driverComplianceReadiness(d),
      })),
    [drivers]
  );

  const complianceSummary = useMemo(
    () => summarizeActiveCompliance(driversWithReadiness),
    [driversWithReadiness]
  );

  const handleComplianceChip = (readiness: ComplianceReadiness | undefined) => {
    setComplianceFilter(readiness);
    if (readiness) setStatusFilter('active');
  };

  const filteredDrivers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return driversWithReadiness.filter((d) => {
      if (complianceFilter) {
        if (d.status !== 'active') return false;
        if (d.readiness !== complianceFilter) return false;
      } else if (statusFilter && d.status !== statusFilter) {
        return false;
      }
      if (!q) return true;
      const name = d.name.toLowerCase();
      const phone = (d.phone || '').toLowerCase();
      const email = (d.email || '').toLowerCase();
      const license = (d.license_number || '').toLowerCase();
      return name.includes(q) || phone.includes(q) || email.includes(q) || license.includes(q);
    });
  }, [driversWithReadiness, statusFilter, complianceFilter, searchQuery]);

  const hasListFilters = !!statusFilter || !!complianceFilter || !!searchQuery.trim();

  useEffect(() => {
    fetchDrivers();
    void supabase
      .from('vehicles')
      .select('*')
      .order('fleet_number')
      .then(({ data }) => setVehicles(data ?? []));
    if (isHqAdmin) {
      void supabase
        .from('fleet_operators')
        .select('*')
        .order('name')
        .then(({ data }) => setOperators(data ?? []));
    }
  }, [isHqAdmin]);

  const fetchDrivers = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('drivers')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        message.error(error.message);
      } else {
        setDrivers(data || []);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchDrivers();
  }, []);

  const handleCreate = () => {
    setEditingDriver(null);
    form.resetFields();
    if (!isHqAdmin && operatorId) {
      form.setFieldsValue({ fleet_operator_id: operatorId });
    }
    setLicenseFile(null);
    setLicensePreview(null);
    setModalVisible(true);
  };

  const handleEdit = (record: Driver) => {
    setEditingDriver(record);
    form.setFieldsValue({
      ...record,
      preferred_vehicle_id: record.preferred_vehicle_id ?? undefined,
      license_expiry: record.license_expiry ? dayjs(record.license_expiry) : null,
      wwcc_expiry: record.wwcc_expiry ? dayjs(record.wwcc_expiry) : null,
    });
    setLicenseFile(null);
    setLicensePreview(null);
    setModalVisible(true);
    if (record.license_photo_url) {
      void (async () => {
        if (record.license_photo_url!.startsWith('http')) {
          setLicensePreview(record.license_photo_url!);
        } else {
          const signed = await getSignedUrl('drivers', record.license_photo_url!, 3600);
          if (signed) setLicensePreview(signed);
        }
      })();
    }
  };

  const handleView = async (record: Driver) => {
    setViewDriver(record);
    setViewLicenseUrl(null);

    if (record.license_photo_url) {
      if (record.license_photo_url.startsWith('http')) {
        setViewLicenseUrl(record.license_photo_url);
      } else {
        const signed = await getSignedUrl('drivers', record.license_photo_url, 3600);
        setViewLicenseUrl(signed);
      }
    }
  };

  const handleDelete = (id: string) => {
    localeConfirm(t, {
      title: t('drivers.messages.deleteTitle'),
      content: t('drivers.messages.deleteContent'),
      okText: t('drivers.actions.delete'),
      okType: 'danger',
      onOk: async () => {
        const { error } = await supabase
          .from('drivers')
          .delete()
          .eq('id', id);

        if (error) {
          message.error(formatAdminDbError(error, i18n.language, 'delete'));
        } else {
          message.success(t('drivers.messages.deleted'));
          fetchDrivers();
        }
      }
    });
  };

  const uploadLicensePhoto = async (file: File): Promise<string | null> => {
    setLicenseUploading(true);
    try {
      const compressed = await compressImage(file);
      const fileName = `driver-licenses/${Date.now()}-${Math.random().toString(36).substring(2)}.jpg`;

      const { data, error } = await supabase.storage
        .from('drivers')
        .upload(fileName, compressed, {
          cacheControl: '3600',
          upsert: false
        });

      if (error) {
        message.error(t('drivers.messages.uploadFailed', { message: error.message }));
        return null;
      }

      return data.path;
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('drivers.messages.uploadFailedGeneric'));
      return null;
    } finally {
      setLicenseUploading(false);
    }
  };

  const handleSubmit = async (values: Record<string, unknown>) => {
    try {
      const licenseExpiry = values.license_expiry as dayjs.Dayjs | null | undefined;
      const expiryYmd = licenseExpiry ? licenseExpiry.format('YYYY-MM-DD') : null;
      const wwccExpiry = values.wwcc_expiry as dayjs.Dayjs | null | undefined;
      const wwccYmd = wwccExpiry ? wwccExpiry.format('YYYY-MM-DD') : null;
      const nextStatus = String(values.status ?? 'active');
      if (nextStatus === 'active' && isExpiryPast(expiryYmd)) {
        const ok = await confirmActiveExpired(t, {
          title: t('drivers.messages.activeExpiredConfirmTitle'),
          content: t('drivers.messages.activeExpiredConfirmContent'),
        });
        if (!ok) return;
      }

      let licensePhotoPath: string | null = editingDriver?.license_photo_url || null;
      if (licenseFile) {
        const uploadedPath = await uploadLicensePhoto(licenseFile);
        if (uploadedPath) {
          licensePhotoPath = uploadedPath;
        }
      }

      const driverData = {
        ...values,
        email: (() => {
          const raw = values.email;
          if (raw == null || String(raw).trim() === '') return null;
          return String(raw).trim().toLowerCase();
        })(),
        license_expiry: expiryYmd,
        wwcc_expiry: wwccYmd,
        license_photo_url: licensePhotoPath,
        preferred_vehicle_id: (values.preferred_vehicle_id as string | undefined) || null,
      };

      delete (driverData as Record<string, unknown>).initial_password;

      if (editingDriver) {
        const { error } = await supabase
          .from('drivers')
          .update(driverData)
          .eq('id', editingDriver.id);

        if (error) {
          message.error(error.message);
        } else {
          if (!editingDriver.auth_uid && values.phone && canProvision) {
            try {
              const auth = await provisionAuthUser({
                role: 'driver',
                email: phoneToAuthEmail(String(values.phone)),
                phone: String(values.phone),
                driverId: editingDriver.id,
                redirectTo: driverResetPasswordRedirectUrl(),
              });
              localeSuccess(t, {
                title: t('drivers.messages.updated'),
                width: 560,
                content: (
                  <SetupCredentialsNotice
                    email={auth.email}
                    setupLink={auth.setupLink}
                    extraNote={t('drivers.messages.setupLinkPhoneHint')}
                  />
                ),
              });
            } catch (authErr: unknown) {
              const msg = authErr instanceof Error ? authErr.message : 'Failed to provision login';
              message.warning(t('drivers.messages.loginFailed', { message: msg }));
            }
          } else {
            message.success(t('drivers.messages.updated'));
          }
          setModalVisible(false);
          await afterMutation(editingDriver.id, fetchDrivers);
        }
      } else {
        const { data: inserted, error } = await supabase
          .from('drivers')
          .insert([driverData])
          .select('id')
          .single();

        if (error) {
          message.error(error.message);
          return;
        }

        if (canProvision) {
          try {
            const auth = await provisionAuthUser({
              role: 'driver',
              email: phoneToAuthEmail(String(values.phone)),
              phone: String(values.phone),
              driverId: inserted.id,
              redirectTo: driverResetPasswordRedirectUrl(),
            });

            if (auth.setupLink) {
              try {
                await invokeEdgeFunction('send-driver-invite', {
                  body: {
                    name: values.name,
                    phone: values.phone,
                    driverId: inserted.id,
                    setupLink: auth.setupLink,
                  },
                });
              } catch (inviteErr) {
                console.error('Failed to send driver invite:', inviteErr);
                message.warning(t('drivers.messages.inviteFailed'));
              }
            }

            localeSuccess(t, {
              title: t('drivers.messages.created'),
              width: 560,
              content: (
                <SetupCredentialsNotice
                  email={auth.email}
                  setupLink={auth.setupLink}
                  extraNote={t('drivers.messages.setupLinkPhoneHint')}
                />
              ),
            });
          } catch (authErr: unknown) {
            await supabase.from('drivers').delete().eq('id', inserted.id);
            const msg = authErr instanceof Error ? authErr.message : 'Failed to create login account';
            message.error(t('drivers.messages.rolledBack', { message: msg }));
            return;
          }
        } else {
          message.success(t('drivers.messages.created'));
        }

        setModalVisible(false);
        await afterMutation(inserted.id, fetchDrivers);
      }
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('drivers.messages.saveFailed'));
    }
  };

  const handleSendSetupLink = (record: Driver) => {
    if (!canProvision) {
      message.warning(t('drivers.messages.setupLinkNoPermission'));
      return;
    }
    localeConfirm(t, {
      title: t('drivers.actions.sendSetupLink'),
      content: t('drivers.messages.setupLinkConfirm', { name: record.name }),
      okText: t('drivers.actions.sendSetupLink'),
      onOk: async () => {
        try {
          const result = await sendPasswordSetupLink({
            driverId: record.id,
            redirectTo: driverResetPasswordRedirectUrl(),
            sendEmail: true,
          });
          logAssistResetAudit({
            actorId: user?.id,
            targetType: 'driver',
            targetId: record.id,
            channel: result.emailed ? 'email_link' : 'link_only',
            emailed: result.emailed,
          });
          localeSuccess(t, {
            title: t('drivers.messages.setupLinkSent'),
            width: 560,
            content: (
              <SetupCredentialsNotice
                email={result.email}
                setupLink={result.setupLink}
                extraNote={
                  result.emailed
                    ? t('drivers.messages.setupLinkEmailed', { email: result.recipient || '' })
                    : t('drivers.messages.setupLinkPhoneHint')
                }
              />
            ),
          });
        } catch (err) {
          message.error(err instanceof Error ? err.message : t('drivers.messages.setupLinkFailed'));
        }
      },
    });
  };

  const handleLicensePhotoChange = async (file: File) => {
    if (!validateImageFile(file)) {
      message.error(t('drivers.messages.onlyImages'));
      return false;
    }

    try {
      const compressed = await compressImage(file);
      setLicenseFile(compressed);
      const reader = new FileReader();
      reader.onload = (e) => {
        setLicensePreview(e.target?.result as string);
      };
      reader.readAsDataURL(compressed);
    } catch (err: unknown) {
      message.error(err instanceof Error ? err.message : t('drivers.messages.invalidImage'));
    }
    return false;
  };

  type DriverRow = Driver & { readiness: ComplianceReadiness };

  const columns: TableProps<DriverRow>['columns'] = [
    {
      title: t('drivers.columns.name'),
      dataIndex: 'name',
      key: 'name',
      width: 160,
      fixed: 'left',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: t('drivers.columns.readiness'),
      dataIndex: 'readiness',
      key: 'readiness',
      width: 120,
      defaultSortOrder: 'ascend',
      sorter: (a, b) =>
        complianceReadinessRank(a.readiness) - complianceReadinessRank(b.readiness),
      render: (readiness: ComplianceReadiness) => (
        <Tag color={complianceTagColor(readiness)}>{t(`compliance.${readiness}`)}</Tag>
      ),
    },
    {
      title: t('drivers.columns.phone'),
      dataIndex: 'phone',
      key: 'phone',
      width: 140,
    },
    {
      title: t('drivers.columns.email'),
      dataIndex: 'email',
      key: 'email',
      width: 180,
      ellipsis: true,
      render: (v: string | null | undefined) => v?.trim() || '—',
    },
    {
      title: t('drivers.columns.licenseNumber'),
      dataIndex: 'license_number',
      key: 'license_number',
      width: 140,
      ellipsis: true,
    },
    {
      title: t('drivers.columns.licenseExpiry'),
      dataIndex: 'license_expiry',
      key: 'license_expiry',
      width: 130,
      render: (val: string) => (val ? formatDate(val) : '—'),
      sorter: (a, b) => a.license_expiry.localeCompare(b.license_expiry)
    },
    {
      title: t('drivers.columns.wwccExpiry'),
      dataIndex: 'wwcc_expiry',
      key: 'wwcc_expiry',
      width: 130,
      render: (val: string | null | undefined) => (val ? formatDate(val) : '—'),
      sorter: (a, b) => (a.wwcc_expiry || '').localeCompare(b.wwcc_expiry || ''),
    },
    {
      title: t('drivers.columns.operator'),
      dataIndex: 'fleet_operator_id',
      key: 'fleet_operator_id',
      width: 150,
      ellipsis: true,
      render: (id: string) => operatorNameById[id] ?? '—',
    },
    {
      title: t('drivers.columns.preferredVehicle'),
      dataIndex: 'preferred_vehicle_id',
      key: 'preferred_vehicle_id',
      width: 180,
      ellipsis: true,
      render: (id: string | null | undefined) => (id ? vehicleLabelById[id] ?? '—' : '—'),
    },
    {
      title: t('drivers.columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status: string) => <StatusTag status={status} />,
    },
    {
      title: t('drivers.columns.acceptingTasks'),
      dataIndex: 'accepting_tasks',
      key: 'accepting_tasks',
      width: 100,
      render: (v: boolean | undefined) => (
        <Tag color={v ? 'success' : 'default'}>
          {v ? t('drivers.accepting.yes') : t('drivers.accepting.no')}
        </Tag>
      ),
      filters: [
        { text: t('drivers.accepting.yes'), value: true },
        { text: t('drivers.accepting.no'), value: false },
      ],
      onFilter: (value, record) => Boolean(record.accepting_tasks) === value,
    },
    {
      title: t('drivers.columns.actions'),
      key: 'actions',
      width: canProvision || isHqAdmin ? 280 : 180,
      fixed: 'right',
      render: (_, record) => (
        <TableRowActions>
          <Button type="link" icon={<EyeOutlined />} onClick={() => void handleView(record)}>
            {t('drivers.actions.view')}
          </Button>
          {canEdit ? (
            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
              {t('drivers.actions.edit')}
            </Button>
          ) : null}
          {canProvision && record.auth_uid ? (
            <Button
              type="link"
              icon={<KeyOutlined />}
              onClick={() => handleSendSetupLink(record)}
            >
              {t('drivers.actions.sendSetupLink')}
            </Button>
          ) : null}
          {canDelete ? (
            <Button type="link" danger icon={<DeleteOutlined />} onClick={() => handleDelete(record.id)}>
              {t('drivers.actions.delete')}
            </Button>
          ) : null}
        </TableRowActions>
      ),
    },
  ];

  return (
    <>
      <AdminListPage
        title={t('drivers.title')}
        subtitle={t('drivers.subtitle')}
        extra={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
              {t('drivers.add')}
            </Button>
          ) : undefined
        }
        summary={
          <ComplianceSummaryBar
            summary={complianceSummary}
            activeFilter={complianceFilter}
            onSelect={handleComplianceChip}
          />
        }
        filters={
          <Space wrap>
            <Input.Search
              allowClear
              placeholder={t('drivers.placeholders.search')}
              style={{ width: 240 }}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onSearch={setSearchQuery}
            />
            <Select
              placeholder={t('drivers.placeholders.allStatuses')}
              allowClear
              style={{ minWidth: 160 }}
              value={statusFilter}
              disabled={!!complianceFilter}
              onChange={(value) => {
                setStatusFilter(value);
                if (value && value !== 'active') setComplianceFilter(undefined);
              }}
              options={[
                { value: 'active', label: t('drivers.status.active') },
                { value: 'inactive', label: t('drivers.status.inactive') },
              ]}
            />
          </Space>
        }
        resultCount={filteredDrivers.length}
        hasActiveFilters={hasListFilters}
        onClearFilters={() => {
          setStatusFilter(undefined);
          setComplianceFilter(undefined);
          setSearchQuery('');
        }}
      >
        <Table
          dataSource={filteredDrivers}
          columns={columns}
          loading={loading}
          rowKey="id"
          pagination={pagination}
          onChange={onTableChange}
          rowClassName={rowClassName}
          scroll={{ x: 1300 }}
          locale={{
            emptyText: hasListFilters ? t('drivers.emptyFiltered') : t('drivers.empty'),
          }}
        />
      </AdminListPage>

      <Modal
        title={editingDriver ? t('drivers.edit') : t('drivers.add')}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        okText={editingDriver ? t('drivers.actions.save') : t('drivers.actions.create')}
        cancelText={t('drivers.actions.cancel')}
        width={560}
        confirmLoading={licenseUploading}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={(v) => void handleSubmit(v)} requiredMark>
          {/* Habit order: name → phone → licence expiry → licence # → emergency */}
          <Form.Item
            name="name"
            label={t('drivers.fields.name')}
            rules={[{ required: true, message: t('drivers.validation.name') }]}
          >
            <Input placeholder={t('drivers.placeholders.name')} autoFocus={!editingDriver} />
          </Form.Item>
          <Form.Item
            name="phone"
            label={t('drivers.fields.phone')}
            rules={[{ required: true, message: t('drivers.validation.phone') }]}
          >
            <Input placeholder={t('drivers.placeholders.phone')} />
          </Form.Item>
          <Form.Item
            name="email"
            label={t('drivers.fields.email')}
            rules={[
              { type: 'email', message: t('drivers.validation.email') },
            ]}
            extra={t('drivers.fields.emailHint')}
          >
            <Input placeholder={t('drivers.placeholders.email')} allowClear />
          </Form.Item>
          <Form.Item
            name="license_expiry"
            label={t('drivers.fields.licenseExpiry')}
            rules={[{ required: true, message: t('drivers.validation.licenseExpiry') }]}
          >
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="wwcc_expiry" label={t('drivers.fields.wwccExpiry')}>
            <DatePicker style={{ width: '100%' }} allowClear />
          </Form.Item>
          <Form.Item
            name="license_number"
            label={t('drivers.fields.licenseNumber')}
            rules={[{ required: true, message: t('drivers.validation.licenseNumber') }]}
          >
            <Input placeholder={t('drivers.placeholders.licenseNumber')} disabled={!!editingDriver} />
          </Form.Item>
          <Form.Item
            name="emergency_contact_name"
            label={t('drivers.fields.emergencyContactName')}
            rules={[{ required: true, message: t('drivers.validation.emergencyName') }]}
          >
            <Input placeholder={t('drivers.placeholders.emergencyName')} />
          </Form.Item>
          <Form.Item
            name="emergency_contact_phone"
            label={t('drivers.fields.emergencyPhone')}
            rules={[{ required: true, message: t('drivers.validation.emergencyPhone') }]}
          >
            <Input placeholder={t('drivers.placeholders.emergencyPhone')} />
          </Form.Item>

          {editingDriver && isHqAdmin ? (
            <Form.Item
              name="fleet_operator_id"
              label={t('drivers.fields.operator')}
              rules={[{ required: true, message: t('drivers.validation.operator') }]}
            >
              <Select
                options={operators.map((o) => ({ value: o.id, label: o.name }))}
                placeholder={t('drivers.placeholders.operator')}
                onChange={() => form.setFieldsValue({ preferred_vehicle_id: undefined })}
              />
            </Form.Item>
          ) : (
            <>
              <Form.Item
                name="fleet_operator_id"
                hidden
                rules={[{ required: true, message: t('drivers.validation.operator') }]}
              >
                <Input type="hidden" />
              </Form.Item>
              <Typography.Paragraph type="secondary" style={{ marginTop: -4, marginBottom: 16 }}>
                {t('drivers.operatorReadonly', {
                  name:
                    (isHqAdmin
                      ? operators.find((o) => o.id === watchedOperatorId)?.name
                      : operator?.name) || '—',
                })}
              </Typography.Paragraph>
            </>
          )}

          {editingDriver ? (
            <Form.Item name="status" label={t('drivers.fields.status')} initialValue="active">
              <Select
                options={[
                  { value: 'active', label: t('drivers.status.active') },
                  { value: 'inactive', label: t('drivers.status.inactive') },
                ]}
              />
            </Form.Item>
          ) : (
            <Form.Item name="status" hidden initialValue="active">
              <Input type="hidden" />
            </Form.Item>
          )}

          <Collapse
            ghost
            style={{ marginLeft: -12 }}
            defaultActiveKey={editingDriver ? ['advanced'] : undefined}
            items={[
              {
                key: 'advanced',
                label: (
                  <Typography.Text style={{ fontWeight: 500 }}>
                    {t('drivers.formSections.advanced')}
                  </Typography.Text>
                ),
                children: (
                  <>
                    {isHqAdmin && !editingDriver ? (
                      <Form.Item label={t('drivers.changeOperator')}>
                        <Select
                          value={watchedOperatorId}
                          options={operators.map((o) => ({ value: o.id, label: o.name }))}
                          placeholder={t('drivers.placeholders.operator')}
                          onChange={(value) => {
                            form.setFieldsValue({
                              fleet_operator_id: value,
                              preferred_vehicle_id: undefined,
                            });
                          }}
                        />
                      </Form.Item>
                    ) : null}
                    <Form.Item label={t('drivers.fields.licensePhoto')}>
                      <div style={{ marginBottom: 8 }}>
                        {licensePreview && (
                          <img
                            src={licensePreview}
                            alt={t('drivers.preview')}
                            style={{
                              width: '100%',
                              maxHeight: 200,
                              objectFit: 'cover',
                              borderRadius: 8,
                              marginBottom: 8,
                            }}
                          />
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/jpg,image/png"
                          onChange={(e) => {
                            const file = e.target.files?.[0];
                            if (file) void handleLicensePhotoChange(file);
                          }}
                        />
                      </div>
                    </Form.Item>
                    <Form.Item
                      name="preferred_vehicle_id"
                      label={t('drivers.fields.preferredVehicle')}
                      extra={
                        preferredVehicleOptions.length === 0
                          ? t('drivers.emptyPreferredVehicle')
                          : undefined
                      }
                    >
                      <Select
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        options={preferredVehicleOptions}
                        placeholder={t('drivers.placeholders.preferredVehicle')}
                        disabled={preferredVehicleOptions.length === 0}
                      />
                    </Form.Item>
                    <Form.Item name="notes" label={t('drivers.fields.notes')}>
                      <Input.TextArea rows={3} placeholder={t('drivers.placeholders.notes')} />
                    </Form.Item>
                    {canProvision ? (
                      <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
                        {t('drivers.passwordHelp.setupLinkOnly')}
                      </Typography.Paragraph>
                    ) : null}
                  </>
                ),
              },
            ]}
          />
        </Form>
      </Modal>

      <EntityDrawer
        open={!!viewDriver}
        onClose={() => setViewDriver(null)}
        title={viewDriver ? viewDriver.name : t('drivers.details')}
        items={
          viewDriver
            ? [
                { key: 'name', label: t('drivers.columns.name'), children: viewDriver.name },
                {
                  key: 'readiness',
                  label: t('drivers.columns.readiness'),
                  children: (
                    <Tag color={complianceTagColor(driverComplianceReadiness(viewDriver))}>
                      {t(`compliance.${driverComplianceReadiness(viewDriver)}`)}
                    </Tag>
                  ),
                },
                { key: 'phone', label: t('drivers.columns.phone'), children: viewDriver.phone },
                {
                  key: 'email',
                  label: t('drivers.columns.email'),
                  children: viewDriver.email?.trim() || '—',
                },
                { key: 'license', label: t('drivers.columns.licenseNumber'), children: viewDriver.license_number },
                { key: 'expiry', label: t('drivers.columns.licenseExpiry'), children: formatDate(viewDriver.license_expiry) },
                {
                  key: 'wwcc',
                  label: t('drivers.columns.wwccExpiry'),
                  children: viewDriver.wwcc_expiry ? formatDate(viewDriver.wwcc_expiry) : '—',
                },
                {
                  key: 'preferred_vehicle',
                  label: t('drivers.fields.preferredVehicle'),
                  children: viewDriver.preferred_vehicle_id
                    ? vehicleLabelById[viewDriver.preferred_vehicle_id] ?? '—'
                    : '—',
                },
                {
                  key: 'accepting_tasks',
                  label: t('drivers.columns.acceptingTasks'),
                  children: (
                    <Tag color={viewDriver.accepting_tasks ? 'success' : 'default'}>
                      {viewDriver.accepting_tasks ? t('drivers.accepting.yes') : t('drivers.accepting.no')}
                    </Tag>
                  ),
                },
                {
                  key: 'accepting_tasks_at',
                  label: t('drivers.columns.acceptingTasksAt'),
                  children: viewDriver.accepting_tasks_at
                    ? formatDateTime(viewDriver.accepting_tasks_at)
                    : '—',
                },
                {
                  key: 'accepting_tasks_location',
                  label: t('drivers.columns.acceptingLocation'),
                  children: (() => {
                    const pt = parseGeoPoint(viewDriver.accepting_tasks_location);
                    if (!pt) return '—';
                    const label = `${pt.lat.toFixed(5)}, ${pt.lng.toFixed(5)}`;
                    return (
                      <Typography.Link
                        href={`https://www.google.com/maps?q=${pt.lat},${pt.lng}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {label}
                      </Typography.Link>
                    );
                  })(),
                },
                { key: 'emergency_name', label: t('drivers.fields.emergencyContact'), children: viewDriver.emergency_contact_name },
                { key: 'emergency_phone', label: t('drivers.fields.emergencyPhone'), children: viewDriver.emergency_contact_phone },
                {
                  key: 'status',
                  label: t('drivers.columns.status'),
                  children: <StatusTag status={viewDriver.status} />,
                },
                { key: 'notes', label: t('drivers.fields.notes'), children: viewDriver.notes || '—' },
                { key: 'created', label: t('drivers.columns.created'), children: formatDateTime(viewDriver.created_at) },
                {
                  key: 'updated',
                  label: t('drivers.columns.updated'),
                  children: formatDateTime(viewDriver.updated_at),
                },
                ...(viewLicenseUrl
                  ? [{
                      key: 'license_photo',
                      label: t('drivers.fields.licensePhoto'),
                      children: (
                        <img
                          src={viewLicenseUrl}
                          alt="License"
                          style={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 8 }}
                        />
                      ),
                    }]
                  : []),
              ]
            : undefined
        }
      />
    </>
  );
}

export default Drivers;
